import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DashboardMyCredit } from "../../../components/backgrounds/DashboardMyCredit";
import { Forms } from "../../../components/backgrounds/Forms";
import { validateSessionAndJwtToken } from "../../../utils/validateTokenSession";
import { returnToSignin } from "../../../utils/returnToSign";
import { HttpRequest } from "../../../services/httpRequest";
import { VeriffService } from "../../../services/veriff/veriff.service";
import { useCredit } from "../../../context/creditContext";

export function VeriffStatusPage() {
  const navigate = useNavigate();
  const { token } = useParams();
  const isPublicLink = Boolean(token);
  const { disclosures, creditInfo, due } = useCredit();
  const [basicInfo, setBasicInfo] = useState({});
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [status, setStatus] = useState("LOADING"); // LOADING, PENDING, APPROVED, DECLINED, TIMEOUT
  const [reason, setReason] = useState("");

  const pollingIntervalRef = useRef(null);
  const attemptsRef = useRef(0);
  const maxAttempts = 36; // 36 attempts * 5 seconds = 180 seconds (3 minutes)

  // Ref con la version mas reciente de fetchVerificationStatus, para que
  // startPolling (memoizada con deps []) pueda invocarla sin necesitar su
  // referencia como dependencia y sin romper el setInterval en curso.
  const fetchVerificationStatusRef = useRef(null);

  // startPolling depende solo de refs y setState (todo estable), por lo que
  // puede memoizarse con deps [] e invocar siempre la version mas reciente de
  // fetchVerificationStatus a traves del ref, rompiendo la dependencia
  // circular entre ambas funciones.
  const startPolling = useCallback(() => {
    pollingIntervalRef.current = setInterval(async () => {
      attemptsRef.current += 1;

      if (attemptsRef.current >= maxAttempts) {
        setStatus("TIMEOUT");
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
        return;
      }

      await fetchVerificationStatusRef.current?.();
    }, 5000); // Poll every 5 seconds
  }, []);

  const fetchVerificationStatus = useCallback(async () => {
    try {
      const response = await VeriffService.getStatus();
      const backendStatus = response?.status; // PENDING, APPROVED, DECLINED

      if (backendStatus === "APPROVED") {
        setStatus("APPROVED");
        if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
      } else if (backendStatus === "DECLINED") {
        setStatus("DECLINED");
        setReason(response?.session?.reason || "Discrepancia en la validación de identidad.");
        if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
      } else if (backendStatus === "PENDING") {
        setStatus("PENDING");
        // Start polling if not already started
        if (!pollingIntervalRef.current) {
          startPolling();
        }
      } else {
        // Fallback for unexpected backend responses
        setStatus("PENDING");
        if (!pollingIntervalRef.current) {
          startPolling();
        }
      }
    } catch (error) {
      console.error("Error fetching Veriff status:", error);
      // In case of API failure (network issue, etc.), we don't break immediately, just let polling retry
      if (!pollingIntervalRef.current) {
        setStatus("PENDING");
        startPolling();
      }
    }
  }, [startPolling]);

  // Mantiene el ref sincronizado con la version mas reciente en cada render.
  fetchVerificationStatusRef.current = fetchVerificationStatus;

  // Protect route and load user info (el flujo de enlace público no consulta sesión ni estado en BD)
  useEffect(() => {
    if (isPublicLink) {
      return;
    }

    const checkAuthAndInit = async () => {
      try {
        const isValidToken = await validateSessionAndJwtToken();
        if (!isValidToken) {
          returnToSignin(navigate);
          return;
        }

        const data = await HttpRequest.getInfoUser(localStorage.getItem("doc"));
        setBasicInfo(data.message.info);
        setIsLoadingProfile(false);

        // Start checking verification status
        fetchVerificationStatus();
      } catch (error) {
        console.error("Auth check failed:", error);
        localStorage.removeItem("doc");
        localStorage.setItem("is_signin", false);
        returnToSignin(navigate);
      }
    };

    checkAuthAndInit();

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
    // `fetchVerificationStatus` ahora esta memoizada con useCallback y su unica
    // dependencia (`startPolling`) tambien es estable (useCallback([]) + ref
    // para invocar siempre la version mas reciente), por lo que su referencia
    // no cambia entre renders y puede incluirse aqui sin producir un loop.
  }, [navigate, isPublicLink, fetchVerificationStatus]);

  const handleRetry = () => {
    navigate("/veriff");
  };

  // Enlace público (sin sesión): layout de la plantilla de "flujo" (Forms + NavbarLogin),
  // no el dashboard autenticado, para no aparentar que el visitante ya inició sesión.
  if (isPublicLink) {
    return (
      <div className="bg-purpleLight min-h-screen pt-32 pb-10">
        <Forms
          title="Verificación de identidad"
          description="Gracias por completar la verificación."
          form={
            <div className="shadow-lg overflow-hidden rounded-md">
              <div className="px-4 py-8 bg-white sm:p-8 text-center">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
                  <svg
                    className="h-10 w-10 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold mb-4 text-green-600">
                  ¡Proceso completado!
                </h2>
                <p className="text-grayP mb-8">
                  Ya recibimos tu verificación de identidad. Puedes cerrar esta ventana
                  y continuar la conversación en WhatsApp.
                </p>
                <button
                  onClick={() => window.close()}
                  className="w-full py-4 px-6 bg-darkBg text-white rounded-xl font-bold text-lg hover:bg-darkBg/90 transition duration-300 shadow-md cursor-pointer"
                >
                  Cerrar ventana
                </button>
              </div>
            </div>
          }
        />
      </div>
    );
  }

  if (isLoadingProfile) {
    return (
      <DashboardMyCredit
        name={"Cargando"}
        balance={0}
        current={"4"}
        title={"Verificación"}
        disclosures={disclosures}
        creditInfo={creditInfo}
        due={due}
        main={
          <div className="flex flex-col items-center justify-center min-h-[300px]">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-darkBg mb-4"></div>
            <p className="text-grayP">Cargando perfil...</p>
          </div>
        }
      />
    );
  }

  return (
    <DashboardMyCredit
      name={basicInfo?.firstname || "Usuario"}
      balance={0}
      current={"4"}
      title={"Estado de Verificación"}
      disclosures={disclosures}
      creditInfo={creditInfo}
      due={due}
      main={
        <div className="pb-6 mt-10 mb-32 md:mb-0 mx-6 lg:mx-0">
          <div className="rounded-2xl transition shadow-lg p-8 bg-white max-w-xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <img
                src="/svg/imagotipo-galilea-20.svg"
                alt="Galilea Logo"
                className="h-16 w-auto"
              />
            </div>

            {/* PENDING STATE */}
            {status === "PENDING" && (
              <div className="py-4">
                <div className="flex flex-col items-center justify-center py-6">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-darkBg mb-6"></div>
                  <h2 className="text-2xl font-bold mb-4 text-tdark">
                    Validando tu identidad
                  </h2>
                  <p className="text-grayP max-w-sm mx-auto mb-4">
                    Estamos procesando la validación de tus datos y cruzando la información con nuestras bases de datos de seguridad.
                  </p>
                  <p className="text-sm text-grayP font-semibold bg-gray-50 px-4 py-2 rounded-full inline-block">
                    Esto suele tardar menos de 3 minutos. Por favor no cierres esta página...
                  </p>
                </div>
              </div>
            )}

            {/* TIMEOUT STATE */}
            {status === "TIMEOUT" && (
              <div className="py-4">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-50 mb-6">
                  <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold mb-4 text-tdark">
                  Tu verificación está en proceso
                </h2>
                <p className="text-grayP mb-8">
                  El análisis de seguridad está tomando un poco más de tiempo de lo habitual. No te preocupes, no necesitas esperar aquí. Te notificaremos en cuanto finalice el proceso.
                </p>
                <button
                  onClick={() => navigate("/perfil")}
                  className="w-full py-4 px-6 bg-darkBg text-white rounded-xl font-bold text-lg hover:bg-darkBg/90 transition duration-300 shadow-md cursor-pointer"
                >
                  Entendido, ir al Perfil
                </button>
              </div>
            )}

            {/* APPROVED STATE */}
            {status === "APPROVED" && (
              <div className="py-4">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
                  <svg
                    className="h-10 w-10 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold mb-4 text-green-600">
                  ¡Identidad Verificada!
                </h2>
                <p className="text-grayP mb-8">
                  Tus datos han sido validados correctamente. Cumples con todos los requisitos de verificación de identidad.
                </p>
                <button
                  onClick={() => navigate("/perfil")}
                  className="w-full py-4 px-6 bg-darkBg text-white rounded-xl font-bold text-lg hover:bg-darkBg/90 transition duration-300 shadow-md cursor-pointer"
                >
                  Continuar
                </button>
              </div>
            )}

            {/* DECLINED / FAILED STATE */}
            {status === "DECLINED" && (
              <div className="py-4">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
                  <svg
                    className="h-10 w-10 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold mb-4 text-red-600">
                  Verificación Rechazada
                </h2>
                <div className="bg-red-50 text-red-700 rounded-xl p-4 mb-8 text-sm border border-red-100 max-w-md mx-auto">
                  <p className="font-semibold mb-1">Razón del rechazo:</p>
                  <p>{reason}</p>
                </div>
                <div className="flex flex-col gap-4">
                  <button
                    onClick={handleRetry}
                    className="w-full py-4 px-6 bg-darkBg text-white rounded-xl font-bold text-lg hover:bg-darkBg/90 transition duration-300 shadow-md cursor-pointer"
                  >
                    Volver a intentar
                  </button>
                  <button
                    onClick={() => navigate("/perfil")}
                    className="text-sm text-grayP hover:text-tdark underline cursor-pointer"
                  >
                    Ir al Perfil
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      }
    />
  );
}
